











































































































































import { Component, Prop } from 'vue-property-decorator'
import { mixins } from 'vue-class-component'
import { ExStore } from 'vuex'
import dayjs from 'dayjs'
import { DispensationMedicineState } from '@/store/dispensation/types'
import { DateString } from '@/types/utils'
import { DispensationMedicine } from '@/types/dispensation'
import { formatDispayDate, getAllDatesOfMonth } from '@/utils/datetime'
import Form from '@/mixins/form'
import {
  DispensatinMedicineInputType,
  DispensatinMedicineInputListType
} from '@/components/dispensationCheckTab/DispensationMedicine.vue'

@Component({
  components: {
    DispensationMedicine: () =>
      import('@/components/dispensationCheckTab/DispensationMedicine.vue'),
    SelectInput: () => import('@/components/common/inputs/SelectInput.vue'),
    Loading: () => import('@/components/common/loadings/Index.vue')
  }
})
export default class DispensationCheckForm extends mixins(Form) {
  @Prop(Number) patientId!: number
  @Prop(Number) dispensationId!: number

  $store!: ExStore

  inputPrescriptionDateYear: null | number = null
  inputPrescriptionDateMonth: null | number = null
  inputPrescriptionDateDay: null | number = null
  inputDispensationMedicines = this.defaultMedicineInputs
  pendingUpdate = false

  get dispensation() {
    return this.$store.getters['dispensation/dispensations'][
      this.dispensationId
    ]
  }

  get medicineGroups() {
    return this.$store.getters['dispensation/medicinesGroupedByRp'][
      this.dispensationId
    ]
  }

  get dispensationMedicines() {
    return this.$store.getters['dispensation/medicines']
  }

  get rpNumbers() {
    return Object.keys(this.medicineGroups)
      .map(Number)
      .sort((rp1, rp2) => rp1 - rp2)
  }

  get prescriptionDate() {
    return (
      this.dispensation.prescriptionDate &&
      dayjs(this.dispensation.prescriptionDate)
    )
  }

  get dispensationDate() {
    return dayjs(this.dispensation.dispensationDate)
  }

  get prescriptionYearOptins() {
    const end = this.dispensationDate.year()
    return [end - 1, end]
  }

  get prescriptionMonthOptins() {
    if (!this.inputPrescriptionDateYear) {
      return []
    }

    const endDate = this.dispensationDate
    const endMonth = endDate.month() + 1

    return this.inputPrescriptionDateYear === endDate.year()
      ? [...new Array(endMonth)].map((_, i) => 1 + i)
      : [...new Array(13 - endMonth)].map((_, i) => endMonth + i)
  }

  get prescriptionDayOptins() {
    if (!this.inputPrescriptionDateYear || !this.inputPrescriptionDateMonth) {
      return []
    }

    const options = getAllDatesOfMonth(
      this.inputPrescriptionDateYear,
      this.inputPrescriptionDateMonth
    )

    const dispensationYear = this.dispensationDate.year()
    const dispensationMonth = this.dispensationDate.month() + 1
    const dispensationDay = this.dispensationDate.date()

    if (
      this.inputPrescriptionDateYear === dispensationYear &&
      this.inputPrescriptionDateMonth === dispensationMonth
    ) {
      return options.filter(o => o <= dispensationDay)
    }

    return options
  }

  get errorMessage() {
    const key = Object.keys(this.validator.messages).find(
      key => this.validator.messages[key]
    )
    return key ? this.validator.messages[key] : ''
  }

  get defaultMedicineInputs() {
    const keys = [
      'isOnedose',
      'remain',
      'isSuggestion',
      'suggestionNote'
    ] as Array<keyof DispensationMedicineState>

    return this.dispensation.medicines.reduce(
      (inputsGroup, id) =>
        Object.assign(inputsGroup, {
          [id]: keys.reduce(
            (inputs, key) =>
              Object.assign(inputs, {
                [key]: this.dispensationMedicines[id][key]
              }),
            {} as DispensatinMedicineInputType
          )
        }),
      {} as DispensatinMedicineInputListType
    )
  }

  formatDispayDate(dateString: DateString) {
    return formatDispayDate(dateString)
  }

  extractDispensationMedicineInputs(medicines: DispensationMedicine[]) {
    return medicines.reduce(
      (inputs, medicine) =>
        Object.assign(inputs, {
          [medicine.id]: this.inputDispensationMedicines[medicine.id]
        }),
      {} as DispensatinMedicineInputListType
    )
  }

  isSelectPrescriptionDate() {
    return (
      this.inputPrescriptionDateYear !== null &&
      this.inputPrescriptionDateMonth !== null &&
      this.inputPrescriptionDateDay !== null
    )
  }

  get selectedPrescriptionDate() {
    if (this.isSelectPrescriptionDate()) {
      return dayjs(
        new Date(
          this.inputPrescriptionDateYear as number,
          (this.inputPrescriptionDateMonth as number) - 1,
          this.inputPrescriptionDateDay as number
        )
      ).format('YYYY-MM-DD') as DateString
    }

    return null
  }

  buildParameters() {
    const prescriptionDate = this.selectedPrescriptionDate
    const medicines = Object.keys(this.inputDispensationMedicines)
      .map(Number)
      .map(id => ({
        ...this.dispensationMedicines[id],
        ...this.inputDispensationMedicines[id]
      }))

    return {
      prescriptionDate,
      medicines
    }
  }

  async handleClickSubmitButton() {
    if (this.validator.hasAnyError()) {
      return
    }

    try {
      this.pendingUpdate = true

      const payload = this.buildParameters()

      await this.$store.dispatch('dispensation/patch', {
        payload,
        patientId: this.patientId,
        dispensationId: this.dispensationId
      })

      this.inputDispensationMedicines = this.defaultMedicineInputs
    } catch (error) {
      console.error(error)
    } finally {
      this.pendingUpdate = false
    }
  }

  buildInstruction() {
    const inputs = this.inputDispensationMedicines
    const prescriptionDate = this.selectedPrescriptionDate || '（処方日未設定）'
    const messages = [`${prescriptionDate}の処方について`]

    this.rpNumbers.forEach(rpNo => {
      const { id } = this.medicineGroups[rpNo][0]
      const { suggestionNote } = inputs[id]

      if (!suggestionNote) {
        return
      }

      const medicinNames = this.medicineGroups[rpNo].map(
        m => `${m.medicineName}`
      )

      messages.push('')
      messages.push(`Rp ${rpNo}`)
      messages.push(`薬品名: ${medicinNames.join('、')}`)
      messages.push(suggestionNote)
    })

    return messages.join('\n')
  }

  handleChangePrescriptionDateYear(value: number | null) {
    if (value === null) {
      this.inputPrescriptionDateMonth = null
      this.inputPrescriptionDateDay = null
    } else if (
      !this.inputPrescriptionDateMonth ||
      this.prescriptionMonthOptins.indexOf(this.inputPrescriptionDateMonth) ===
        -1
    ) {
      this.inputPrescriptionDateMonth = this.prescriptionMonthOptins[0]
      this.inputPrescriptionDateDay = this.prescriptionDayOptins[0]
    } else if (
      !this.inputPrescriptionDateDay ||
      this.prescriptionDayOptins.indexOf(this.inputPrescriptionDateDay) === -1
    ) {
      this.inputPrescriptionDateDay = this.prescriptionDayOptins[0]
    }
  }

  handleChangePrescriptionDateMonth(value: number | null) {
    if (value === null) {
      this.inputPrescriptionDateDay = null
    } else if (
      !this.inputPrescriptionDateDay ||
      this.prescriptionDayOptins.indexOf(this.inputPrescriptionDateDay) === -1
    ) {
      this.inputPrescriptionDateDay = this.prescriptionDayOptins[0]
    }
  }

  handleCreateInstructionButton() {
    const text = this.buildInstruction()
    this.$store.commit('preInputs/set', { text })
    const preInputToken = this.$store.getters['preInputs/token']

    this.$router.push({
      name: 'ScheduleCreate',
      params: { patientId: String(this.patientId) },
      query: { preInputToken }
    })
  }

  created() {
    if (this.prescriptionDate) {
      this.inputPrescriptionDateYear = this.prescriptionDate.year()
      this.inputPrescriptionDateMonth = this.prescriptionDate.month() + 1
      this.inputPrescriptionDateDay = this.prescriptionDate.date()
    }
  }
}
